import React, { useState, useEffect, useRef, FC } from "react";
import { Box, IconName } from "@telia/teddy";
import _find from "lodash/find";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _size from "lodash/size";
import { connect, useDispatch, useSelector } from "react-redux";
import CollapsedMemberCard, { MemberCardButton, MemberCardInfo } from "./CollapsedMemberCard";
import ExpandedMemberCard from "./ExpandedMemberCard";
import MemberCardStep from "./MemberCardStep";
import MemberPortingCard from "./MemberPortingCard";
import { getDiscountPrice, getPrice } from "./utils";
import {
    CUSTOMER_TYPE,
    FORM_ID,
    CustomerType,
    CART_ITEM_TYPE as CART_ITEM_TYPE_CONST,
    DeliveryType,
    MAX_PRICE_FOR_NEW_NUMBER,
} from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import {
    Form,
    ICartItemWithBundleId,
    RootState,
    EsimType,
    ICartItem,
    CART_ITEM_TYPE,
    ChangedService,
    Discount,
} from "../../../../../models";
import { PhoneCheckResultMessage } from "../../../../../models/phoneNumberCheck";
import { AccountMessage, StepId, SubscriptionsState } from "../../../../../models/state";
import { getFormattedPrice } from "../../../../../shared/priceFormatter";
import { isRunningOnClientSide } from "../../../../../shared/util";
import { getFeatureToggles } from "../../../app/state/selectors";
import { PhoneNumberFormatters } from "../../../app/utils";
import { trackCheckoutAndNavigate } from "../../../google-analytics";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import * as NAVIGATION_SELECTORS from "../../../navigation/state/selectors";
import { fetchWebdeals } from "../../../webdeals/state/actions";
import * as WEBDEAL_SELECTORS from "../../../webdeals/state/selectors";
import LeaseLimitModal from "../../components/molecules/LeaseLimitModal/LeaseLimitModal";
import ServiceModal from "../../components/molecules/ServicesModal/ServicesModal";
import SmartDiscountModal from "../../components/molecules/SmartDiscountModal/SmartDiscountModal";
import WebdealModal from "../../components/molecules/WebdealModal/WebdealModal";
import ChoosePhoneNumber from "../../components/organisms/ChoosePhoneNumber/ChoosePhoneNumber";
import ChooseSimCard from "../../components/organisms/ChooseSimCard/ChooseSimCard";
import { usePrevious } from "../../hooks";
import {
    chooseMemberPhoneNumber,
    prefillMemberform,
    chooseMemberPhoneNumberSwitchPanel,
    fetchAvailableNumbers,
    setMemberDetails,
    setSelectedStep,
    setSelectedPhoneNumber,
    checkMemberPhoneNumber,
} from "../../state/actions/addMemberActions";
import { addWebdealAccessory, setActiveSubscription } from "../../state/actions/cartActions";
import { fetchDeliveryAndPaymentMethods } from "../../state/actions/combinedActions";
import { checkoutPayment } from "../../state/actions/paymentActions";
import { setValue as setPersonalDetailsValue } from "../../state/actions/personalDetailsActions";
import {
    CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
    CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS,
} from "../../state/actionTypes";
import { getCurrentSubscriptionMemberMessages } from "../../state/selectors/accountSelectors";
import {
    getHardwareEsimTypeForCustomerType,
    hasViaplayCampaign,
    getActiveSubscription,
    getAllItemsOfType,
    getItemByBundleId,
    isSubscriptionDraft as isSubscriptionDraftSelector,
    getCartItemBySku,
    hasItemsWithIsWebdeal,
    isEsimOnlyCompatible,
    hasOnlyServiceSubscriptions as hasOnlyServiceSubscriptionsSelector,
    getSubscriptionItemFromBundleId,
    hasDraftMobileSubscriptions as hasDraftMobileSubscriptionsSelector,
} from "../../state/selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../../state/selectors/combinedSelectors";
import * as CUSTOMER_INFO_SELECTORS from "../../state/selectors/customerInfoSelectors";
import * as DELIVERY_INFO_SELECTORS from "../../state/selectors/deliveryInfoSelectors";
import {
    getFormById,
    showAddMemberInputFields as showAddMemberInputFieldsSelector,
} from "../../state/selectors/formSelectors";
import { getSelectedStep } from "../../state/selectors/memberSelector";
import { getPaymentSuccessRedirectUrl } from "../../state/selectors/paymentSelectors";
import { getPhoneNumberCheckType, getSelectedPhoneNumber } from "../../state/selectors/phoneNumberSelectors";
import {
    getProductUser,
    getProductUserFullName,
    getSubscriptionName,
    isPhoneNumberSelectedAtPDP as isPhoneNumberSelectedAtPDPSelector,
    isSecretNumber as isSecretNumberSelector,
    isSubscriptionBroadband as isSubscriptionBroadbandSelector,
    isServiceSubscription as isServiceSubscriptionSelector,
    getMobileSubscriptions,
    getSubscription,
    getChangedServices,
} from "../../state/selectors/subscriptionsSelectors";
import TEXT from "../../text-consts";
import { fulfilled, isPhoneNumberUsable } from "../../utils";

const stepOrder: Record<StepId, number> = {
    phoneNumber: 1,
    simCard: 2,
    portinDate: 3,
    info: 4,
};

interface Props extends BaseProps {
    index: number;
    subscriptionId: string;
}
interface BaseProps {
    activeSubscription?: string;
    campaignId?: string;
    changedServices: ChangedService[];
    currentBundleItem: ICartItemWithBundleId;
    currentSubscriptionMemberMessages: AccountMessage[];
    customerType?: CustomerType;
    deliveryInfoCode?: string;
    deliveryInfoNote?: string;
    deliveryInfoType?: DeliveryType;
    draftSubscriptions: ICartItem[];
    esimSelected: boolean;
    esimType: EsimType;
    hardwareEsimType?: EsimType;
    hasDraftSubscriptions: boolean;
    hasOnlyServiceSubscriptions: boolean;
    hasProductUser: boolean;
    hasViaplayCampaignInCartItem: boolean;
    hasWebdealsInCart: boolean;
    isBarringsUsed: boolean;
    isCampaignAllowed: boolean;
    isCampaignToggled: boolean;
    isCatalogListingOk: boolean;
    isEmailTakenForTeliaSky?: boolean;
    isEmailTakenForTeliaTrygg?: boolean;
    isEsimOnly?: boolean;
    isFetchingData: boolean;
    isNavigating: boolean;
    isOptingOutForTeliaXServices?: boolean;
    isOrderExtraSimEnabled: boolean;
    isPhoneNumberSelectedAtPDP: boolean;
    isProductUserAgeOk: boolean;
    isProductUserChild: boolean;
    isProductUserDifferentThanLegalOwner: boolean;
    isProductUserYoung: boolean;
    isSecretNumber: boolean;
    isServiceSubscription: boolean;
    isSmartDiscountPriceLowerThanNormalPrice: boolean;
    isSubscriptionBroadband: boolean;
    isSubscriptionDraft: boolean;
    memberForm: Form;
    paymentSuccessRedirectUrl: string;
    productUserFullName: string;
    productUserPhoneNumber: string;
    selectedStep?: StepId;
    showAddMemberInputFields: boolean;
    skuByBundleId: string | null;
    subscriptionName: string;
    subscriptions: SubscriptionsState;
    totalPaymentPrice: number;
    webdeals: Discount[];
}

const Member: FC<Props> = ({
    subscriptionId,
    index,
    // Redux
    memberForm,
    isFetchingData,
    isNavigating,
    currentSubscriptionMemberMessages,
    showAddMemberInputFields,
    isCatalogListingOk,
    isBarringsUsed,
    isEsimOnly,
    isOrderExtraSimEnabled,
    isCampaignToggled,
    isPhoneNumberSelectedAtPDP,
    isProductUserAgeOk,
    isProductUserDifferentThanLegalOwner,
    subscriptions,
    activeSubscription,
    isProductUserChild,
    isProductUserYoung,
    isSmartDiscountPriceLowerThanNormalPrice,
    currentBundleItem,
    skuByBundleId,
    productUserFullName,
    customerType,
    esimType,
    esimSelected,
    isSecretNumber,
    hardwareEsimType,
    hasProductUser,
    draftSubscriptions,
    campaignId,
    isCampaignAllowed,
    hasViaplayCampaignInCartItem,
    productUserPhoneNumber,
    isSubscriptionBroadband,
    isSubscriptionDraft,
    isServiceSubscription,
    subscriptionName,
    changedServices,
    selectedStep,
    hasWebdealsInCart,
    isEmailTakenForTeliaTrygg,
    isEmailTakenForTeliaSky,
    isOptingOutForTeliaXServices,
    totalPaymentPrice,
    paymentSuccessRedirectUrl,
    hasOnlyServiceSubscriptions,
    webdeals,
    deliveryInfoCode,
    deliveryInfoNote,
    deliveryInfoType,
    hasDraftSubscriptions,
}) => {
    const [showWebdealModal, setShowWebdealModal] = useState(false);
    const [showSmartDiscountModal, setShowSmartDiscountModal] = useState(false);
    const numberOfSubscriptions = _size(subscriptions);
    const subscriptionItem = useSelector((state: RootState) => getSubscription(state, subscriptionId));
    const subscriptionCartItem = useSelector((state: RootState) =>
        getSubscriptionItemFromBundleId(state, subscriptionId),
    );
    const getCartItemTypeBySku = useSelector((state: RootState) =>
        skuByBundleId ? getCartItemBySku(state, skuByBundleId)?.type : undefined,
    );
    const getCartItem = useSelector((state: RootState) =>
        skuByBundleId ? getCartItemBySku(state, skuByBundleId) : undefined,
    );
    const [showServicesModal, setShowServicesModal] = useState(false);
    const [showLeaseLimitModal, setShowLeaseLimitModal] = useState(false);
    // The modal's own resubmit must not open it again, e.g. when the chosen upfront amount equals the minimum.
    const hasSeenLeaseLimitModal = useRef(false);
    const [isCreditCheckUnavailable, setIsCreditCheckUnavailable] = useState(false);
    const [leaseLimitMaxAmount, setLeaseLimitMaxAmount] = useState(MAX_PRICE_FOR_NEW_NUMBER);
    const [isDoingPhoneNumberCheckOnLoad, setIsDoingPhoneNumberCheckOnLoad] = useState(false);
    const discountCustomerType =
        customerType === CUSTOMER_TYPE.PORT_IN || customerType === CUSTOMER_TYPE.EXISTING_OTHER
            ? CUSTOMER_TYPE.NEW
            : customerType;
    const shouldShowSimOptions =
        customerType &&
        (
            [
                CUSTOMER_TYPE.NEW,
                CUSTOMER_TYPE.PORT_IN,
                CUSTOMER_TYPE.NEW_OR_PORT_IN,
                CUSTOMER_TYPE.EXISTING_OTHER,
            ] as readonly string[]
        ).includes(customerType);

    const showMemberCard = subscriptionId === activeSubscription;

    const postCode = _get(memberForm, "values.member.postcode", "");

    const formatter = isSubscriptionBroadband ? PhoneNumberFormatters[3].displayFn : PhoneNumberFormatters[0].displayFn;
    const hash = isRunningOnClientSide() ? window.location.hash.replace("#", "") : "";
    const isSubscriptionItem = [
        CART_ITEM_TYPE.SUBSCRIPTION_DRAFT,
        CART_ITEM_TYPE.SUBSCRIPTION,
        CART_ITEM_TYPE.SPECIALPRODUCT,
    ].includes(subscriptionItem?.type || "");
    const isEsimCompatible =
        !_isEmpty(hardwareEsimType) ||
        (isSubscriptionItem && (!_isEmpty(getCartItem?.supportedEsimTypes) || !skuByBundleId));

    const showPortinStep = isSubscriptionItem && customerType === CUSTOMER_TYPE.PORT_IN;

    const dispatch = useDispatch();
    const prevPaymentSuccessRedirectUrl = usePrevious(paymentSuccessRedirectUrl);

    useEffect(() => {
        if (paymentSuccessRedirectUrl && paymentSuccessRedirectUrl !== prevPaymentSuccessRedirectUrl) {
            dispatch(trackCheckoutAndNavigate(2, paymentSuccessRedirectUrl));
        }
    }, [paymentSuccessRedirectUrl, prevPaymentSuccessRedirectUrl, trackCheckoutAndNavigate]);

    useEffect(() => {
        const memberIdFromUrl = new URLSearchParams(hash).get("edit");
        if (memberIdFromUrl) {
            dispatch(prefillMemberform(memberIdFromUrl, false, true));
            dispatch(setActiveSubscription(memberIdFromUrl));
            dispatch(setPersonalDetailsValue("isCampaignToggled", hasViaplayCampaignInCartItem));
            history.replaceState(null, "", window.location.pathname);
        }

        // By default show 1 subscription expanded
        if (hasProductUser && !memberIdFromUrl && numberOfSubscriptions === 1) {
            editMember();
        }

        //eslint-disable-next-line
    }, []);

    const handleOnContinue = async (error?: { error: string }) => {
        if (error) {
            // Do nothing but show error on page
            setShowWebdealModal(false);
            return;
        }

        if (
            hasOnlyServiceSubscriptions &&
            totalPaymentPrice <= 0 &&
            (!hasDraftSubscriptions || _size(draftSubscriptions) === 1)
        ) {
            await dispatch(fetchDeliveryAndPaymentMethods());
            await dispatch(checkoutPayment(deliveryInfoType, deliveryInfoCode, deliveryInfoNote));
            return;
        }

        setShowWebdealModal(false);
        closeMember();
    };

    const shouldShowWebdealModal = (deals: Discount[]): boolean => {
        if (
            deals.length > 1 ||
            deals[0]?.additionalProducts.productNumbers.length > 1 ||
            deals[0]?.additionalProducts.variants[0].discountedPrice > 0
        ) {
            return true;
        }
        return false;
    };

    const editMember = async () => {
        dispatch(prefillMemberform(subscriptionId, false, true));
        dispatch(setActiveSubscription(subscriptionId));
        dispatch(setPersonalDetailsValue("isCampaignToggled", hasViaplayCampaignInCartItem));
        dispatch(setPersonalDetailsValue("esimType", hardwareEsimType));
        dispatch(setPersonalDetailsValue("isEsimSelected", esimSelected));
        dispatch(setPersonalDetailsValue("isSecretNumber", isSecretNumber));
        dispatch(setPersonalDetailsValue("isCatalogListingOk", isCatalogListingOk));
        dispatch(setPersonalDetailsValue("isOptingOutForTeliaXServices", false));
        dispatch(setPersonalDetailsValue("isEmailTakenForTeliaTrygg", false));
        dispatch(setPersonalDetailsValue("isEmailTakenForTeliaSky", false));
        dispatch(setSelectedStep(undefined));
    };

    const closeMember = () => {
        const draftSubscription = _find(draftSubscriptions, (sub) => sub.bundleId !== subscriptionId);
        if (draftSubscription) {
            dispatch(setPersonalDetailsValue("isProductUserDifferentThanLegalOwner", true));
            dispatch(setPersonalDetailsValue("isCatalogListingOk", false));
            dispatch(setPersonalDetailsValue("isSecretNumber", false));
            dispatch(setPersonalDetailsValue("isCampaignToggled", false));
            dispatch(setPersonalDetailsValue("campaignIdsNotAllowed", []));
            dispatch(setPersonalDetailsValue("isOptingOutForTeliaXServices", false));
            dispatch(setPersonalDetailsValue("isEmailTakenForTeliaTrygg", false));
            dispatch(setPersonalDetailsValue("isEmailTakenForTeliaSky", false));
            dispatch(setSelectedPhoneNumber("", CUSTOMER_TYPE.NEW, draftSubscription.bundleId));
            dispatch({
                type: fulfilled(CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS),
                payload: [],
            });
            dispatch({
                type: CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
                activePanel: CUSTOMER_TYPE.EXISTING,
            });
            dispatch(setActiveSubscription(draftSubscription.bundleId));
            dispatch(fetchAvailableNumbers(true, draftSubscription.bundleId));
            dispatch(prefillMemberform(draftSubscription.bundleId, true));
            dispatch(setSelectedStep("phoneNumber"));
            return;
        }

        dispatch(setActiveSubscription(""));
        dispatch(setSelectedStep(undefined));
    };

    const updateMemberDetails = async () => {
        const response = await dispatch(
            setMemberDetails({
                memberId: subscriptionId,
                campaignId,
                isOptingOutForTeliaXServices,
            }),
        );

        const hasMinUpfrontPrice = typeof response.minUpfrontPrice === "number" && response.minUpfrontPrice > 0;
        if ((response.isNewNumberWithPotentialFraud || hasMinUpfrontPrice) && !hasSeenLeaseLimitModal.current) {
            const totalPrice = Number(currentBundleItem?.price?.total ?? 0);
            setLeaseLimitMaxAmount(
                hasMinUpfrontPrice ? Math.max(0, totalPrice - response.minUpfrontPrice!) : MAX_PRICE_FOR_NEW_NUMBER,
            );
            setIsCreditCheckUnavailable(!!response.isCreditCheckUnavailable);
            setShowLeaseLimitModal(true);
        }

        return response.hasUpdatedMember;
    };

    const onSubmitMemberDetails = async () => {
        const discountSubscriptionType = currentBundleItem?.discount?.discountSubscriptionType || [];
        const upCaseDiscountCustomerType = discountCustomerType ? discountCustomerType.toUpperCase() : "";
        const isSmartDiscount = currentBundleItem?.discount?.isSmartDiscount;
        if (
            !showSmartDiscountModal &&
            isSmartDiscount &&
            isSmartDiscountPriceLowerThanNormalPrice &&
            !discountSubscriptionType.includes(upCaseDiscountCustomerType)
        ) {
            setShowSmartDiscountModal(true);
            return;
        }

        const hasUpdatedMember = await updateMemberDetails();

        if (hasUpdatedMember) {
            // No SKU for current bundleId? Just continue on.
            if (!skuByBundleId) {
                return handleOnContinue();
            }

            let deals: Discount[] = [];
            if (hasWebdealsInCart) {
                const webdealData = await dispatch(fetchWebdeals(skuByBundleId));
                deals = _get(webdealData, "action.payload.webdeals", []);
            }
            if (shouldShowWebdealModal(deals)) {
                setShowWebdealModal(true);
            } else if (deals.length === 1 && deals[0].additionalProducts.variants.length >= 1) {
                await dispatch(
                    addWebdealAccessory(
                        [deals[0].additionalProducts.variants[0].sku],
                        undefined,
                        false,
                        subscriptionId,
                        deals[0].code,
                    ),
                );
                handleOnContinue();
            } else {
                handleOnContinue();
            }
        }
    };

    useEffect(() => {
        if (showMemberCard) {
            const element = document.getElementById(`beta-checkout-heading-${index}`);
            const elementPosition = element?.getBoundingClientRect().top || 0;
            const offsetPosition = elementPosition + window.pageYOffset - 120;

            const id = setTimeout(() => {
                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                });
            }, 200);
            return () => {
                clearTimeout(id);
            };
        }
    }, [index, showMemberCard]);

    const handleOnClose = () => {
        setShowSmartDiscountModal(false);
    };

    const proceedFromPhoneNumberStep = async () => {
        const shouldGoToSimCard = isSubscriptionDraft || shouldShowSimOptions;

        if (shouldGoToSimCard) {
            dispatch(setSelectedStep("simCard"));
        } else if (isSubscriptionDraft) {
            dispatch(setSelectedStep("info"));
        } else {
            await updateMemberDetails();
            dispatch(setSelectedStep(undefined));
        }
    };

    useEffect(() => {
        if (isServiceSubscription && showMemberCard && subscriptionId) {
            dispatch(setSelectedStep("info"));
            if (isSubscriptionDraft) {
                dispatch(chooseMemberPhoneNumberSwitchPanel(CUSTOMER_TYPE.NEW, subscriptionId));
            }
        }
    }, [isServiceSubscription, subscriptionId, showMemberCard, dispatch]);

    // Perform phone number check on load if user already selected phone number at PDP
    useEffect(() => {
        if (!isPhoneNumberSelectedAtPDP) {
            return;
        }

        async function performPhoneNumberCheck() {
            setIsDoingPhoneNumberCheckOnLoad(true);
            const result = await dispatch(checkMemberPhoneNumber(productUserPhoneNumber, subscriptionId, "existing"));
            setIsDoingPhoneNumberCheckOnLoad(false);
            const allowedToProceed = _get(result, "value.allowedToProceed");
            const messages: PhoneCheckResultMessage[] = _get(result, "value.messages", []);

            // If phoneNumberCheck was okay -> Skip to second step
            if (allowedToProceed && messages.every((message) => message.type === "success")) {
                dispatch(setSelectedStep("simCard"));
            }
        }

        if (
            showMemberCard &&
            subscriptionId &&
            !isServiceSubscription &&
            productUserPhoneNumber &&
            isPhoneNumberUsable(productUserPhoneNumber) &&
            isSubscriptionDraft
        ) {
            performPhoneNumberCheck();
        }
    }, [
        isPhoneNumberSelectedAtPDP,
        showMemberCard,
        subscriptionId,
        isServiceSubscription,
        productUserPhoneNumber,
        dispatch,
    ]);

    if (isDoingPhoneNumberCheckOnLoad) {
        return <></>;
    }

    const isManualDiscount = subscriptionCartItem?.discount?.isManualDiscount;
    const discountValueUpfront = isManualDiscount ? 0 : _get(subscriptionCartItem?.discount, "value.upfront") || 0;
    const discountValueMonthly = _get(subscriptionCartItem?.discount, "value.monthly") || 0;
    const discountPrice = getDiscountPrice(subscriptionCartItem?.price);
    const shouldGetFamilyDiscount =
        subscriptionCartItem?.discount?.types?.some(({ id }) => id === "sameGroup") || false;
    const price = getPrice(getFormattedPrice, subscriptionCartItem?.price, discountValueUpfront, discountValueMonthly);

    const hasFilledOutUser = isServiceSubscription
        ? !!productUserFullName
        : !!productUserPhoneNumber && !!productUserFullName;

    const phoneNumberForTitle = isServiceSubscription ? undefined : productUserPhoneNumber;

    const title = hasFilledOutUser
        ? `${productUserFullName}${phoneNumberForTitle ? ` - ${formatter(phoneNumberForTitle)}` : ""}`
        : "Bruker ikke valgt enda";
    const showUserInfo = !isSubscriptionDraft && !!productUserFullName && !!productUserPhoneNumber;

    return (
        <Box className="add-member__container">
            {showLeaseLimitModal && currentBundleItem.leaseMonths && (
                <LeaseLimitModal
                    cartItem={currentBundleItem}
                    open={showLeaseLimitModal}
                    maxAllowedLeaseAmount={leaseLimitMaxAmount}
                    isCreditCheckUnavailable={isCreditCheckUnavailable}
                    onRetryCreditCheck={async () => {
                        setShowLeaseLimitModal(false);
                        await onSubmitMemberDetails();
                    }}
                    close={async (shouldResubmit) => {
                        setShowLeaseLimitModal(false);
                        if (shouldResubmit) {
                            hasSeenLeaseLimitModal.current = true;
                            try {
                                await onSubmitMemberDetails();
                            } finally {
                                hasSeenLeaseLimitModal.current = false;
                            }
                        }
                    }}
                    urlForTracking={PAGES.CHECKOUT.ADD_MEMBER.URL}
                />
            )}
            {!showMemberCard && (
                <CollapsedMemberCard
                    shouldGetFamilyDiscount={shouldGetFamilyDiscount}
                    hasProductUser={hasProductUser}
                    subscriptionName={subscriptionName}
                    productUserFullName={productUserFullName}
                    productUserPhoneNumber={isServiceSubscription ? undefined : productUserPhoneNumber}
                    needsPhoneNumber={!isServiceSubscription}
                    onButtonClick={editMember}
                    buttonIcon="edit"
                    buttonLabel="Endre"
                    formatter={formatter}
                    price={price}
                    discountPrice={discountPrice}
                    isCompleted={hasFilledOutUser}
                    data-di-mask
                />
            )}
            {showMemberCard && (
                <>
                    <div className="add-member__circle-wrapper">
                        <MemberCardInfo
                            shouldGetFamilyDiscount={shouldGetFamilyDiscount}
                            title={showUserInfo ? title : TEXT.MULTIPLE_SUBSCRIPTIONS.ADD_MEMBER}
                            subscriptionName={subscriptionName}
                            price={price}
                            discountPrice={discountPrice}
                            isCompleted={hasProductUser}
                        />
                        {!isSubscriptionDraft && (
                            <MemberCardButton buttonIcon="close" buttonLabel="Lukk" onButtonClick={closeMember} />
                        )}
                    </div>
                    <div className="add-member__collapser-wrapper" data-cy="add-member-collapser">
                        {!isServiceSubscription && (
                            <MemberCardStep
                                isDisabled={!customerType}
                                label="Velg nummer"
                                onClick={() => dispatch(setSelectedStep("phoneNumber"))}
                                stepId="phoneNumber"
                                selectedStep={selectedStep}
                            >
                                <ChoosePhoneNumber
                                    choosePhoneNumber={(phoneNumber) =>
                                        dispatch(chooseMemberPhoneNumber(phoneNumber, subscriptionId))
                                    }
                                    choosePhoneNumberSwitchPanel={(activePanel) =>
                                        dispatch(chooseMemberPhoneNumberSwitchPanel(activePanel, subscriptionId))
                                    }
                                    fetchAvailableNumbers={() => dispatch(fetchAvailableNumbers(true, subscriptionId))}
                                    selectedPhoneNumber={_get(memberForm, "values.member.number")}
                                    messages={currentSubscriptionMemberMessages}
                                    subscriptionId={subscriptionId}
                                    isAllowedToProceed={showAddMemberInputFields}
                                    buttonLabel={isSubscriptionDraft ? "Fortsett" : "Lagre"}
                                    buttonIcon={
                                        isSubscriptionDraft ? ("arrow-down" as IconName) : ("check-mark" as IconName)
                                    }
                                    buttonIconPlacement={isSubscriptionDraft ? "right" : "left"}
                                    onButtonClick={async () => {
                                        if (!_isEmpty(changedServices)) {
                                            setShowServicesModal(true);
                                        } else {
                                            await proceedFromPhoneNumberStep();
                                        }
                                    }}
                                />
                            </MemberCardStep>
                        )}
                        {!isServiceSubscription && (
                            <MemberCardStep
                                isDisabled={
                                    !customerType ||
                                    !showAddMemberInputFields ||
                                    (isSubscriptionDraft &&
                                        selectedStep &&
                                        stepOrder["simCard"] > stepOrder[selectedStep])
                                }
                                label="Velg SIM-kort"
                                onClick={() => dispatch(setSelectedStep("simCard"))}
                                stepId="simCard"
                                selectedStep={selectedStep}
                            >
                                <ChooseSimCard
                                    isEsimOnly={isEsimOnly ?? false}
                                    isEsimSelected={esimSelected}
                                    isEsimCompatible={isEsimCompatible}
                                    isOrderExtraSimEnabled={isOrderExtraSimEnabled}
                                    shouldShowSimOptions={shouldShowSimOptions || false}
                                    getCartItemTypeBySku={getCartItemTypeBySku}
                                    esimType={esimType}
                                    setPersonalDetailsValue={(...args) => dispatch(setPersonalDetailsValue(...args))}
                                    onButtonClick={async () => {
                                        if (isSubscriptionDraft) {
                                            if (showPortinStep) {
                                                dispatch(setSelectedStep("portinDate"));
                                            } else {
                                                dispatch(setSelectedStep("info"));
                                            }
                                        } else {
                                            const result = await updateMemberDetails();
                                            if (result) {
                                                dispatch(setSelectedStep(undefined));
                                            }
                                        }
                                    }}
                                    buttonLabel={isSubscriptionDraft ? "Fortsett" : "Lagre"}
                                    buttonIcon={
                                        isSubscriptionDraft ? ("arrow-down" as IconName) : ("check-mark" as IconName)
                                    }
                                    buttonIconPlacement={isSubscriptionDraft ? "right" : "left"}
                                    subscriptionId={subscriptionId}
                                    phoneNumber={productUserPhoneNumber}
                                />
                            </MemberCardStep>
                        )}
                        {showPortinStep && (
                            <MemberCardStep
                                isDisabled={
                                    !customerType ||
                                    !showAddMemberInputFields ||
                                    (isSubscriptionDraft &&
                                        selectedStep &&
                                        stepOrder["portinDate"] > stepOrder[selectedStep])
                                }
                                label="Velg ønsket aktiveringsdato"
                                onClick={() => dispatch(setSelectedStep("portinDate"))}
                                stepId="portinDate"
                                selectedStep={selectedStep}
                            >
                                <MemberPortingCard
                                    onContinue={() => dispatch(setSelectedStep("info"))}
                                    subscriptionId={subscriptionId}
                                />
                            </MemberCardStep>
                        )}
                        <MemberCardStep
                            isDisabled={
                                !customerType ||
                                !showAddMemberInputFields ||
                                (isSubscriptionDraft && selectedStep && stepOrder["info"] > stepOrder[selectedStep])
                            }
                            label="Velg bruker"
                            onClick={() => dispatch(setSelectedStep("info"))}
                            stepId="info"
                            selectedStep={selectedStep}
                        >
                            <ExpandedMemberCard
                                setPersonalDetailsValue={(...args) => dispatch(setPersonalDetailsValue(...args))}
                                activeSubscription={activeSubscription}
                                memberForm={memberForm}
                                postCode={postCode}
                                campaignId={campaignId}
                                isCatalogListingOk={isCatalogListingOk}
                                isBarringsUsed={isBarringsUsed}
                                isCampaignToggled={isCampaignToggled}
                                isCampaignAllowed={isCampaignAllowed}
                                isProductUserAgeOk={isProductUserAgeOk}
                                isProductUserDifferentThanLegalOwner={isProductUserDifferentThanLegalOwner}
                                isProductUserChild={isProductUserChild}
                                isProductUserYoung={isProductUserYoung}
                                onSubmitMemberDetails={async () => {
                                    if (isSubscriptionDraft) {
                                        onSubmitMemberDetails();
                                    } else {
                                        const hasUpdatedMember = await updateMemberDetails();
                                        if (hasUpdatedMember) {
                                            if (skuByBundleId) {
                                                let deals: Discount[] = [];
                                                if (hasWebdealsInCart) {
                                                    const webdealData = await dispatch(fetchWebdeals(skuByBundleId));
                                                    deals = _get(webdealData, "action.payload.webdeals", []);
                                                }
                                                if (shouldShowWebdealModal(deals)) {
                                                    setShowWebdealModal(true);
                                                    return;
                                                }
                                            }

                                            dispatch(setSelectedStep(undefined));
                                        }
                                    }
                                }}
                                draftSubscriptions={draftSubscriptions}
                                isFetchingData={isFetchingData}
                                isNavigating={isNavigating}
                                isSubscriptionDraft={isSubscriptionDraft}
                                isEmailTakenForTeliaTrygg={isEmailTakenForTeliaTrygg}
                                isEmailTakenForTeliaSky={isEmailTakenForTeliaSky}
                                isOptingOutForTeliaXServices={isOptingOutForTeliaXServices}
                                shouldSubmitOrder={hasOnlyServiceSubscriptions && totalPaymentPrice <= 0}
                            />
                        </MemberCardStep>
                    </div>
                </>
            )}
            {showServicesModal && (
                <ServiceModal
                    subscriptionId={subscriptionId}
                    onClose={async () => {
                        setShowServicesModal(false);
                        await proceedFromPhoneNumberStep();
                    }}
                />
            )}
            {showWebdealModal && (
                <WebdealModal
                    onContinue={(error) => handleOnContinue(error)}
                    cartItemId={subscriptionId}
                    webdeals={webdeals}
                />
            )}
            {showSmartDiscountModal && (
                <SmartDiscountModal
                    handleOnContinue={() => onSubmitMemberDetails()}
                    onClose={handleOnClose}
                    discountCustomerType={discountCustomerType}
                    currentBundleItem={currentBundleItem}
                />
            )}
        </Box>
    );
};

const mapStateToProps: (state: RootState, ownProps: { subscriptionId: string }) => BaseProps = (
    state,
    { subscriptionId },
) => {
    const activeSubscription = getActiveSubscription(state);
    const campaignId = COMBINED_SELECTORS.getCampaignId();
    return {
        skuByBundleId: COMBINED_SELECTORS.getSkuByBundleId(state, subscriptionId),
        productUserFullName: getProductUserFullName(state, subscriptionId),
        customerType: getPhoneNumberCheckType(state, subscriptionId),
        esimType: CUSTOMER_INFO_SELECTORS.esimType(state),
        esimSelected: CUSTOMER_INFO_SELECTORS.isEsimSelected(state),
        isSecretNumber: isSecretNumberSelector(state, subscriptionId),
        hardwareEsimType: getHardwareEsimTypeForCustomerType(
            state,
            subscriptionId,
            getPhoneNumberCheckType(state, subscriptionId),
        ),
        isEsimOnly: isEsimOnlyCompatible(state, subscriptionId),
        changeType: COMBINED_SELECTORS.getSubscriptionChangeType(state, subscriptionId),
        hasProductUser: !_isEmpty(getProductUser(state, subscriptionId)),
        draftSubscriptions: getAllItemsOfType(state, CART_ITEM_TYPE_CONST.SUBSCRIPTION_DRAFT),
        isSubscriptionDraft: isSubscriptionDraftSelector(state, subscriptionId),
        isServiceSubscription: isServiceSubscriptionSelector(state, subscriptionId),
        campaignId,
        isCampaignAllowed: COMBINED_SELECTORS.getIsCurrentAllowedWithCampaignId(state, campaignId),
        hasViaplayCampaignInCartItem: hasViaplayCampaign(state, subscriptionId),
        productUserPhoneNumber: getSelectedPhoneNumber(state, subscriptionId),
        isSubscriptionBroadband: isSubscriptionBroadbandSelector(state, subscriptionId),
        hasOnlyServiceSubscriptions: hasOnlyServiceSubscriptionsSelector(state),
        subscriptionName: getSubscriptionName(state, subscriptionId),
        isNavigating: NAVIGATION_SELECTORS.isNavigating(state),
        isFetchingData: LOADING_SELECTORS.isLoading(state),
        memberForm: getFormById(state, FORM_ID.ADD_MEMBER),
        currentSubscriptionMemberMessages: getCurrentSubscriptionMemberMessages(state),
        showAddMemberInputFields: showAddMemberInputFieldsSelector(state),
        isCatalogListingOk: CUSTOMER_INFO_SELECTORS.isCatalogListingOk(state),
        isBarringsUsed: CUSTOMER_INFO_SELECTORS.isBarringsUsed(state),
        isCampaignToggled: CUSTOMER_INFO_SELECTORS.isCampaignToggled(state),
        isProductUserAgeOk: CUSTOMER_INFO_SELECTORS.isProductUserAgeOk(state),
        isProductUserDifferentThanLegalOwner: CUSTOMER_INFO_SELECTORS.isProductUserDifferentThanLegalOwner(state),
        isOptingOutForTeliaXServices: CUSTOMER_INFO_SELECTORS.isOptingOutForTeliaXServices(state),
        isEmailTakenForTeliaTrygg: CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaTrygg(state),
        isEmailTakenForTeliaSky: CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaSky(state),
        activeSubscription,
        subscriptions: getMobileSubscriptions(state),
        isProductUserYoung: COMBINED_SELECTORS.isProductUserYoung(state),
        isProductUserChild: COMBINED_SELECTORS.isProductUserChild(state),
        isOrderEsimEnabled: !!getFeatureToggles(state).enableEsimOrder,
        isOrderEsimSimOnlyEnabled: !!getFeatureToggles(state).enableEsimSimOnlyOrder,
        isOrderExtraSimEnabled: !!getFeatureToggles(state).enableExtraSim,
        isPhoneNumberSelectedAtPDP: isPhoneNumberSelectedAtPDPSelector(state, subscriptionId),
        isSmartDiscountPriceLowerThanNormalPrice: COMBINED_SELECTORS.isSmartDiscountPriceLowerThanNormalPrice(state),
        currentBundleItem: getItemByBundleId(state, subscriptionId)[0],
        changedServices: getChangedServices(state, subscriptionId),
        selectedStep: getSelectedStep(state),
        hasWebdealsInCart: hasItemsWithIsWebdeal(state),
        totalPaymentPrice: COMBINED_SELECTORS.getTotalPaymentPrice(state),
        paymentSuccessRedirectUrl: getPaymentSuccessRedirectUrl(state),
        webdeals: WEBDEAL_SELECTORS.getWebdealsState(state),
        deliveryInfoType: DELIVERY_INFO_SELECTORS.getDeliveryInfoType(state),
        deliveryInfoCode: DELIVERY_INFO_SELECTORS.getDeliveryInfoCode(state),
        deliveryInfoNote: DELIVERY_INFO_SELECTORS.getDeliveryInfoNote(state),
        hasDraftSubscriptions: hasDraftMobileSubscriptionsSelector(state),
    };
};

export default connect(mapStateToProps)(Member);
